import { isNotNil } from "client/Misc/utils";
import {
	Mark,
	Node,
	Schema
} from "prosemirror-model";
import { Command, Transaction } from "prosemirror-state";
import { EditLinkResult } from "../EditLinkDialog/EditLinkDialog";
import { LinkMarkAttributes } from "../schema/marks/link";
import { getNodesWithPositionFromSelection, NodeWithPosition } from "../utils";

export function addOrUpdateLinkAtSelection(
	schema: Schema<never, "link">,
	linkData: EditLinkResult,
	didTextChange: boolean
): Command {
	return (state, dispatch): boolean => {
		if (dispatch) {
			const nodesFromSelection: readonly NodeWithPosition[] = getNodesWithPositionFromSelection(
				state,
				{
					markType: schema.marks.link
				}
			);

			const tr: Transaction = state.tr;
			if (nodesFromSelection.length === 1) {
				const [firstNode] = nodesFromSelection;
				const {
					node,
					position
				} = firstNode;

				let marksFromContextForTheNewText: Mark[] | undefined = undefined;

				if (didTextChange) {
					marksFromContextForTheNewText = node.marks
						.filter(m => m.type !== schema.marks.link);
				}

				applyLinkMarkToPosition(
					tr,
					linkData,
					didTextChange,
					position,
					position + node.nodeSize,
					marksFromContextForTheNewText,
					schema
				);
			} else {
				const {
					selection,
					storedMarks
				} = state;

				const {
					from,
					to,
					$from
				} = selection;

				let marksFromContextForTheNewText: readonly Mark[] | undefined = undefined;

				if (didTextChange) {
					marksFromContextForTheNewText = storedMarks
						?? $from.marks();
				}

				applyLinkMarkToPosition(
					tr,
					linkData,
					didTextChange,
					from,
					to,
					marksFromContextForTheNewText,
					schema
				);
			}

			tr.scrollIntoView();

			dispatch(tr);
			return true;
		}

		return true;
	};
}

function applyLinkMarkToPosition(
	tr: Transaction,
	linkData: EditLinkResult,
	didTextChange: boolean,
	from: number,
	to: number,
	marksFromContextForTheNewText: readonly Mark[] | undefined,
	schema: Schema<never, "link">
): void {
	tr.removeMark(from, to, schema.marks.link);
	const {
		href,
		shouldOpenInNewWindow,
		text
	} = linkData;

	const linkAttributes: LinkMarkAttributes = {
		href: href,
		openInANewWindow: shouldOpenInNewWindow
	};

	if (didTextChange) {
		const newTextNode: Node = schema.text(text);
		tr.replaceRangeWith(from, to, newTextNode);

		to = from + newTextNode.nodeSize;

		if (isNotNil(marksFromContextForTheNewText)) {
			for (const mark of marksFromContextForTheNewText) {
				tr.addMark(
					from,
					to,
					mark
				);
			}
		}
	}

	tr.addMark(
		from,
		to,
		schema.marks.link.create(linkAttributes)
	);
}